import type { OrderStatus, PaymentStatus } from '@/features/orders/api';
import {
    DASHBOARD_RANGE,
    type DashboardRange,
    type ReportPeriod,
    type TaxPeriod,
} from '@/features/reports/periods';
import type { ChangeGap } from '@/features/reports/format';
import type {
    AppUsageDays,
    AppUsageReason,
    BrandFigureGap,
    BrandReportState,
    ClientSignal,
    EngagementSignal,
    HeatmapWeeks,
    QueueBucket,
    QueueOrderStatus,
    ReturnCheck,
    ReturnSettlement,
    VersionStanding,
} from '@/features/reports/tabs';
import { api } from '@/lib/api';

/*
  The app version log: one row per sign-in and sign-out from the client app, with the device and
  the versions each carried. Support reads it to answer "what was she running when it broke", so
  it is a lookup and not analysis, and every row is here rather than only each person's newest.

  Paged on the server: the log is a quarter of a million rows in production and grows with every
  sign-in, which is not the shape the registers' one-fetch-and-filter was built for.
*/

export interface AppSession {
    id: number;
    userId: number;
    name: string;
    email: string | null;
    /** The client the session came from, as the legacy names it ("Mobile iOS Application"). */
    platform: string | null;
    osVersion: string | null;
    appVersion: string | null;
    action: string;
    ipAddress: string | null;
    /** Null where the legacy recorded no time. */
    signedInAt: string | null;
}

export interface AppSessionPage {
    rows: AppSession[];
    /** Rows matching the filters, across every page. */
    matched: number;
    /** Rows in the log, before any filter. */
    total: number;
    page: number;
    size: number;
}

export interface AppSessionPerson {
    userId: number;
    name: string;
    /** The names are not unique; this is what tells two people who share one apart. */
    email: string | null;
}

export interface AppSessionFilters {
    page: number;
    size: number;
    q: string;
    userId: number | null;
    action: string | null;
    /** App usage's window and manager (ALS-160); absent is the whole log, as support reads it. */
    days?: AppUsageDays;
    rmId?: number;
}

/*
  Reports › App usage (ALS-160): the app version log read as a report. One response for the whole
  tab, so it loads and fails as one; the API derives every figure from the log, the people with an
  app login and the store release list, and the screen only words them.
*/

/** What App usage is drawn for; absent is the API's default (30 days, every manager). */
export interface AppUsageParams {
    days: AppUsageDays;
    rmId?: number;
}

export interface LatestRelease {
    /** "iOS" or "Android". */
    platform: string;
    version: string;
    releasedOn: string;
}

export interface AppUsageFigures {
    opened: number;
    /** Everyone with an app login, and anyone else who opened it, so `opened` never exceeds it. */
    appLogins: number;
    signIns: number;
    signInsPerPerson: number | null;
    onLatest: number;
    /** A whole percentage of the people who opened it; null when nobody did. */
    onLatestShare: number | null;
    wellBehind: number;
    wellBehindNames: string[];
    passwordResets: number;
}

export interface VersionCount {
    /** Null where the app sent no version. */
    version: string | null;
    people: number;
    isLatest: boolean;
}

export interface PlatformCount {
    platform: string;
    people: number;
}

export interface WorthACallRow {
    userId: number;
    name: string;
    reason: AppUsageReason;
    platform: string | null;
    version: string | null;
    behind: number | null;
    /** The reset for ResetPassword, the last sign-in for NotOpened (null if never). */
    at: string | null;
}

export interface AppPerson {
    userId: number;
    name: string;
    email: string | null;
    /** The latest device: the platform and the OS version it reported. */
    device: string;
    version: string | null;
    standing: VersionStanding;
    behind: number | null;
    signIns: number;
    lastSeenAt: string | null;
    lastAction: string;
}

export interface AppUsageReport {
    days: AppUsageDays;
    /** The first and last day of the window. */
    from: string;
    to: string;
    relationshipManagerId: number | null;
    /** Rows of the log in the window. */
    sessions: number;
    latestReleases: LatestRelease[];
    figures: AppUsageFigures;
    versions: VersionCount[];
    devices: PlatformCount[];
    worthACall: WorthACallRow[];
    people: AppPerson[];
    /** The window's newest rows, all the folded log shows. */
    latestSessions: AppSession[];
}

function appUsageSearch({ days, rmId }: { days?: AppUsageDays; rmId?: number }): URLSearchParams {
    const search = new URLSearchParams();
    if (days !== undefined) search.set('days', String(days));
    if (rmId !== undefined) search.set('rmId', String(rmId));
    return search;
}

/*
  Reports › Client heatmap (ALS-88): three readings of the app's activity log and the log itself,
  one request for the tab so it loads and fails as one. The API decides every score, signal and
  call; the screen only shades and words them. A null score is a week with no activity, never a 0.
*/

/** What the heatmap is drawn for; absent is the API's default (6 weeks, every manager). */
export interface ClientHeatmapParams {
    weeks: HeatmapWeeks;
    rmId?: number;
}

/** A week the log holds: its ISO label ("W43") and its Monday. */
export interface HeatmapWeek {
    label: string;
    starts: string;
}

export interface EngagementRow {
    customerId: number;
    name: string;
    /** One per column; null where the client did nothing that week. */
    scores: (number | null)[];
    signal: EngagementSignal;
}

export interface CallToday {
    customerId: number;
    name: string;
    drop: number;
}

export interface DemandCategory {
    categoryId: number;
    name: string;
}

export interface DemandBrand {
    brandId: number;
    name: string;
    /** One per category; null where there were no views or orders. */
    scores: (number | null)[];
}

export interface Demand {
    categories: DemandCategory[];
    brands: DemandBrand[];
}

export interface ActivityLogRow {
    id: number;
    /** "Zeina Alsaud (Amira Staff)" when her staff did it. */
    client: string;
    module: string;
    area: string;
    action: string;
    reference: string | null;
    /** The colour, when the app sent one. */
    context: string | null;
    viewedOn: string;
}

export interface ActivityLogPage {
    rows: ActivityLogRow[];
    /** The window's log for the manager; `matched` is after the search. */
    total: number;
    matched: number;
    page: number;
    size: number;
}

export interface ActivityLogFilters extends ClientHeatmapParams {
    page: number;
    size: number;
    q: string;
}

export interface ClientHeatmapReport {
    weeks: HeatmapWeeks;
    /** The Monday of the first week shown and the Sunday of the last. */
    from: string;
    to: string;
    relationshipManagerId: number | null;
    /** Only the weeks the log holds, oldest first. */
    columns: HeatmapWeek[];
    engagement: EngagementRow[];
    callToday: CallToday | null;
    demand: Demand;
    /** Rows of the manager's log in the window. */
    logTotal: number;
    /** Its newest rows, all the folded log shows. */
    latestLog: ActivityLogRow[];
}

/*
  Reports › Client heatmap › Lookbook page attention (ALS-89): one lookbook's pages by how often
  clients opened them in the window. A request of its own, so picking a lookbook redraws only the
  card; it takes no manager, since page attention is not per client. The API orders the pages and
  chooses the lookbook (ADR 0105); the screen draws them as they come.
*/

/** What the card is drawn for; no lookbook is the most viewed in the window. */
export interface LookbookAttentionParams {
    weeks: HeatmapWeeks;
    lookbookId?: number;
}

/** A lookbook the picker offers, with every page view it had in the window. */
export interface LookbookChoice {
    lookbookId: number;
    name: string;
    /** When it went live in the app, London wall clock with no offset; books share names. */
    goLive: string;
    views: number;
}

/** One bar: a page by its name or "Page N", and its views; 0 is a page nobody opened. */
export interface PageViews {
    pageId: number;
    position: number;
    label: string;
    views: number;
}

export interface ChosenLookbook {
    lookbookId: number;
    name: string;
    goLive: string;
    /** Most viewed first, a tie in the book's order. */
    pages: PageViews[];
    /** Views of pages since taken out of the book, which have no bar. */
    removedPageViews: number;
}

export interface LookbookAttention {
    weeks: HeatmapWeeks;
    /** The lookbooks opened in the window, most viewed first. */
    lookbooks: LookbookChoice[];
    /** The one drawn; null when nothing was opened and none was asked for. */
    lookbook: ChosenLookbook | null;
}

function heatmapSearch({ weeks, rmId }: ClientHeatmapParams): URLSearchParams {
    const search = new URLSearchParams({ weeks: String(weeks) });
    if (rmId !== undefined) search.set('rmId', String(rmId));
    return search;
}

export const clientHeatmapExportUrl = (params: ClientHeatmapParams) =>
    `/api/reports/client-heatmap/export?${heatmapSearch(params).toString()}`;

/*
  The Reports Overview and the dashboard's Sales card (ALS-85). Every amount is in pounds and
  already summed by the API, which folds both from the same rows: the screens format these and
  never add them up, so the card and the report cannot disagree.
*/

/** A window as the API names it, with the first and the last day it includes. */
export interface SalesWindow {
    label: string;
    from: string;
    to: string;
}

/**
 * A figure and its change from the period before; a margin's change is in points. Where the
 * change is null, `gap` says why; it is null whenever there is a change.
 */
export interface Metric {
    value: number | null;
    change: number | null;
    gap: ChangeGap | null;
}

export interface ToDate {
    label: string;
    revenue: number;
    orders: number;
}

/**
 * One bar of the revenue chart: cost, the refunds settled in the period before VAT, and gross
 * profit after them, which add up to revenue (ALS-411). Gross profit and the margin are after
 * refunds, the same figures as Transactions › Margin.
 */
export interface SalesBucket {
    label: string;
    revenue: number;
    grossProfit: number;
    cost: number;
    refunds: number;
    marginPercentage: number | null;
}

export interface CountRow {
    name: string;
    count: number;
}

export interface RankedRow {
    id: number | null;
    name: string;
    value: number;
}

export interface ReportOverview {
    period: ReportPeriod;
    current: SalesWindow;
    previous: SalesWindow;
    featured: { week: ToDate; month: ToDate; year: ToDate };
    kpis: {
        revenue: Metric;
        grossProfit: Metric;
        marginPercentage: Metric;
        orders: Metric;
        averageOrderValue: Metric;
        units: Metric;
    };
    series: SalesBucket[];
    mix: { occasions: CountRow[]; channels: CountRow[]; types: CountRow[]; statuses: CountRow[] };
    top: {
        clients: RankedRow[];
        brands: RankedRow[];
        suppliers: RankedRow[];
        categories: RankedRow[];
    };
    regions: RankedRow[];
}

export interface DashboardSalesParams {
    range: DashboardRange;
    /** ISO days, read only for a custom range; `to` is the last day included. */
    from?: string;
    to?: string;
}

export interface DashboardSales {
    range: DashboardRange;
    current: SalesWindow;
    previous: SalesWindow;
    revenue: number;
    change: number | null;
    paid: number;
    orders: number;
    averageOrderValue: number | null;
    outstanding: number;
    /** Revenue accumulating across the range; the last point is `revenue`. */
    series: { label: string; revenue: number }[];
}

/*
  Reports › Transactions (ALS-86). Margin, Returns and Products for one period come in one
  response, so they load and fail together; the order queue is a live list with filters of its
  own. Every amount is in pounds and already summed by the API. Refunds come off margin in the
  period they were settled, for what was refunded, so a past period never changes.
*/

export interface MarginKpis {
    orders: Metric;
    averageOrderValue: Metric;
    marginAfterRefunds: Metric;
    refunds: Metric;
    averageMargin: Metric;
    /** Margin after refunds over net after refunds; its change is in points. */
    marginPercentage: Metric;
}

export interface MarginBucket {
    label: string;
    from: string;
    to: string;
    orders: number;
    revenue: number;
    averageOrderValue: number | null;
    marginBeforeRefunds: number;
    /** Refunds settled in the period, before VAT. */
    refunds: number;
    marginAfterRefunds: number;
    averageMargin: number | null;
    marginPercentage: number | null;
}

export interface ProductMargin {
    productId: number;
    name: string;
    brand: string | null;
    units: number;
    net: number;
    marginBeforeRefunds: number;
    refunds: number;
    marginAfterRefunds: number;
    marginPercentage: number | null;
}

export interface ReturnStage {
    count: number;
    value: number;
}

export interface SettledReturn {
    orderLineId: number;
    orderId: number;
    orderNo: string;
    client: string;
    product: string;
    brand: string | null;
    settlement: ReturnSettlement;
    reference: string | null;
    /** For Unmatched, the day the line was completed; null in the before-go-live list. */
    settledOn: string | null;
    amount: number;
}

export interface AwaitingRefund {
    orderLineId: number;
    orderId: number;
    orderNo: string;
    client: string;
    product: string;
    brand: string | null;
    status: 'ReturnRequested' | 'RefundInProgress';
    amount: number;
    parcel: 'Queued' | 'LabelCreated' | 'PickedUp' | 'InTransit' | 'Delivered' | null;
    check: ReturnCheck;
    daysSinceArrival: number | null;
}

export interface TransactionsReport {
    period: ReportPeriod;
    current: SalesWindow;
    previous: SalesWindow;
    margin: { kpis: MarginKpis; series: MarginBucket[] };
    products: ProductMargin[];
    returns: {
        stages: {
            requested: ReturnStage;
            received: ReturnStage;
            completed: ReturnStage;
            unmatched: ReturnStage;
            total: ReturnStage;
        };
        settled: SettledReturn[];
        awaiting: AwaitingRefund[];
        /** Completed before go-live and matched to no refund: in no period and no total. */
        beforeGoLive: SettledReturn[];
        /** Null while the admin is not live, when every unmatched return is before it. */
        goLiveOn: string | null;
    };
}

export interface OrderQueueRow {
    orderId: number;
    orderNo: string;
    customerId: number;
    client: string;
    relationshipManager: string | null;
    value: number;
    status: OrderStatus;
    payment: PaymentStatus;
    bucket: QueueBucket;
    /** Days late for Overdue, days to go for Due. */
    days: number | null;
    dueOn: string | null;
    orderDate: string;
}

export interface OrderQueue {
    rows: OrderQueueRow[];
    /** Per bucket under every filter but the bucket, so the tiles never contradict the table. */
    counts: { bucket: QueueBucket; count: number }[];
    total: number;
}

/** The queue's filters as the URL and the API carry them; absent is not applied. */
export interface OrderQueueParams {
    q?: string;
    bucket?: QueueBucket;
    status?: QueueOrderStatus;
    payment?: PaymentStatus;
    rmId?: number;
    brandId?: number;
    categoryId?: number;
    supplierId?: number;
    from?: string;
    to?: string;
}

/*
  Reports › Transactions › Tax (ALS-90). Every figure is the issued invoices' and credit notes',
  in pounds, already summed by the API: the periods oldest first, the last running to today, each
  with its tax register rows.
*/

export interface TaxRowTotals {
    /** The tax register row as the documents printed it, "VAT 20%". */
    taxRow: string;
    /** Invoiced before VAT, duties included. */
    net: number;
    vat: number;
    refundedNet: number;
    refundedVat: number;
    netTaxable: number;
}

export interface TaxPeriodRow {
    label: string;
    from: string;
    to: string;
    /** Invoiced before VAT, duties left out. */
    netSales: number;
    vat: number;
    refundedVat: number;
    netTaxable: number;
    duties: number;
    totalDue: number;
    rows: TaxRowTotals[];
}

export interface TaxReport {
    period: TaxPeriod;
    current: SalesWindow;
    periods: TaxPeriodRow[];
}

export const taxExportUrl = (period: TaxPeriod) =>
    `/api/reports/tax/export?${new URLSearchParams({ period }).toString()}`;

/*
  Reports › Clients (ALS-87): Spend, New vs returning, Inactive, Drop-off and Cycles for one period
  in one response, so the five views load and fail together. Every amount is in pounds and already
  summed by the API; the definitions of new, returning and inactive are the API's, written once.
*/

export interface ClientBook {
    activeClients: number;
    onTheBook: number;
    averageLifetimeValue: number | null;
    atRiskValue: number;
    atRiskClients: number;
    repeatRate: number | null;
    repeatClients: number;
    orderedClients: number;
}

export interface ClientSignalFlag {
    kind: ClientSignal;
    /** What is owed, or the wallet's balance; null for New and Inactive. */
    amount: number | null;
}

export interface ClientSpendRow {
    customerId: number;
    client: string;
    relationshipManager: string | null;
    /** False for a sale naming a client the customer table lacks: "Unknown client", no record. */
    isKnown: boolean;
    orders: number;
    /** The period's sales. */
    total: number;
    /** Refunded to her in the period, VAT in. */
    refunds: number;
    actualSpend: number;
    lifetime: number;
    signals: ClientSignalFlag[];
}

/** A cohort's figures in a period; refunds before VAT, as margin is. */
export interface CohortFigures {
    clients: number;
    orders: number;
    revenue: number;
    marginBeforeRefunds: number;
    refunds: number;
    marginAfterRefunds: number;
}

export interface ClientMixBucket {
    label: string;
    from: string;
    to: string;
    new: CohortFigures;
    returning: CohortFigures;
}

export interface InactiveClient {
    customerId: number;
    client: string;
    relationshipManager: string | null;
    lastOrderOn: string;
    daysSince: number;
    orders: number;
    lifetime: number;
}

export interface DroppedClient {
    customerId: number;
    client: string;
    relationshipManager: string | null;
    isKnown: boolean;
    lastYearSpend: number;
    lastOrderOn: string;
    lifetime: number;
}

export interface CyclePoint {
    label: string;
    month: string;
    spend: number;
}

/** A run of usual months, 1 to 12, which may wrap from December into January. */
export interface PeakSeason {
    startMonth: number;
    endMonth: number;
}

export interface ClientCycle {
    customerId: number;
    client: string;
    relationshipManager: string | null;
    usualMonths: number[];
    seasons: PeakSeason[];
    nextSeasonOn: string | null;
    usualGapDays: number | null;
    lastOrderOn: string;
    lifetime: number;
}

export interface ClientsReport {
    period: ReportPeriod;
    current: SalesWindow;
    previous: SalesWindow;
    relationshipManagerId: number | null;
    spend: { book: ClientBook; rows: ClientSpendRow[] };
    mix: {
        kpis: {
            newClients: Metric;
            returningClients: Metric;
            newSales: Metric;
            returningSales: Metric;
            newShare: number | null;
        };
        series: ClientMixBucket[];
    };
    inactive: {
        days: number;
        defaultDays: number;
        atRiskValue: number;
        clients: InactiveClient[];
    };
    dropOff: {
        thisYear: SalesWindow;
        lastYear: SalesWindow;
        lastYearClients: number;
        retained: number;
        droppedOff: number;
        dropOffRate: number | null;
        clients: DroppedClient[];
    };
    cycles: {
        recent: CyclePoint[];
        yearBefore: CyclePoint[];
        medianGapDays: number | null;
        lookaheadDays: number;
        startingSoon: ClientCycle[];
        clients: ClientCycle[];
    };
}

/** What the Clients report is drawn for; absent is the API's default. */
export interface ClientsParams {
    period: ReportPeriod;
    inactiveDays?: number;
    rmId?: number;
}

/** The Clients tables an export can be asked for, as the API names them. */
export type ClientsExport =
    'Spend' | 'Mix' | 'Inactive' | 'DropOff' | 'PeakSeason' | 'BuyingMonths';

function clientsSearch({ period, inactiveDays, rmId }: ClientsParams): URLSearchParams {
    const search = new URLSearchParams({ period });
    if (inactiveDays !== undefined) search.set('inactiveDays', String(inactiveDays));
    if (rmId !== undefined) search.set('rmId', String(rmId));
    return search;
}

export const clientsExportUrl = (view: ClientsExport, params: ClientsParams) => {
    const search = clientsSearch(params);
    search.set('view', view);
    return `/api/reports/clients/export?${search.toString()}`;
};

/*
  Reports › Brand intelligence (ALS-91). Every figure is an aggregate over the clientele and none
  names a client. Rates are whole percentages and money is pounds, already worked out: the screen
  formats, it never divides.
*/

/** A calendar quarter: `key` is "2026-Q3", `label` "Q3 2026", `to` the last day it includes. */
export interface ReportQuarter {
    key: string;
    label: string;
    from: string;
    to: string;
    isCurrent: boolean;
    isFirst: boolean;
    beforeData: boolean;
}

export interface BrandOption {
    id: number;
    name: string;
    stocked: boolean;
}

/** The brand register and the quarters, newest first, with the one before the data last. */
export interface ReportBrands {
    brands: BrandOption[];
    quarters: ReportQuarter[];
}

/** A tile's figure, its change against the quarter before, and why either is missing. */
export interface BrandMetric extends Metric {
    unavailable: BrandFigureGap | null;
}

/** One bar of a Brand intelligence chart (ALS-92). */
export interface ChartBar {
    label: string;
    value: number;
}

/** A chart of the pieces bought: no bars, and the reason, when nobody or too few bought. */
export interface BrandChart {
    bars: ChartBar[];
    unavailable: BrandFigureGap | null;
    /** Pieces left out for having no size, or no colour group, the register knows. */
    unrecorded: number;
}

/** A month of the interest trend: `month` is its first day, `index` against the first month at 100. */
export interface TrendPoint {
    month: string;
    interactions: number;
    index: number;
    /** The month has not ended. */
    toDate: boolean;
}

/** The four charts under the tiles (ALS-92), already worked out: the screen only draws them. */
export interface BrandCharts {
    byCategory: ChartBar[];
    sizes: BrandChart;
    colours: BrandChart;
    trend: TrendPoint[];
}

export interface BrandIntelligence {
    brandId: number;
    brand: string;
    /** Active products of the brand. */
    pieces: number;
    quarter: ReportQuarter;
    state: BrandReportState;
    demand: {
        interest: {
            interactions: number;
            previousInteractions: number;
            change: number | null;
            gap: ChangeGap | null;
        };
        sellThrough: {
            percentage: number | null;
            sold: number;
            shown: number;
            toDate: boolean;
            unavailable: BrandFigureGap | null;
        };
        topClients: { engaged: number; of: number; unavailable: BrandFigureGap | null };
    } | null;
    commercial: {
        conversion: BrandMetric;
        averagePrice: BrandMetric;
        repeatRate: BrandMetric;
        walletShare: BrandMetric;
    } | null;
    charts: BrandCharts | null;
}

/** Absent, the quarter is the current one. */
export interface BrandIntelligenceParams {
    brandId: number;
    quarter?: string;
}

/**
 * The brand report PDF for Generate report (ALS-93): the API sets it from the same figures as the
 * tab. Absent, the quarter is the current one.
 */
export const brandReportUrl = ({ brandId, quarter }: BrandIntelligenceParams) => {
    const search = new URLSearchParams({ brandId: String(brandId) });
    if (quarter) search.set('quarter', quarter);
    return `/api/reports/brand-intelligence/pdf?${search.toString()}`;
};

/** The Transactions tables an export can be asked for, as the API names them. */
export type TransactionsExport = 'Margin' | 'Products' | 'Returns' | 'Awaiting' | 'BeforeGoLive';

export function orderQueueSearch(params: OrderQueueParams): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== '') search.set(key, String(value));
    }
    return search.toString();
}

export const transactionsExportUrl = (view: TransactionsExport, period: ReportPeriod) =>
    `/api/reports/transactions/export?${new URLSearchParams({ view, period }).toString()}`;

export const orderQueueExportUrl = (params: OrderQueueParams) => {
    const search = orderQueueSearch(params);
    return `/api/reports/transactions/orders/export${search ? `?${search}` : ''}`;
};

const base = '/api/reports/app-sessions';

export const reportsApi = {
    appSessions: {
        page: ({ page, size, q, userId, action, days, rmId }: AppSessionFilters) => {
            const search = new URLSearchParams({ page: String(page), size: String(size) });
            if (q.trim()) search.set('q', q.trim());
            if (userId !== null) search.set('userId', String(userId));
            if (action !== null) search.set('action', action);
            for (const [key, value] of appUsageSearch({ days, rmId })) search.set(key, value);
            return api<AppSessionPage>(`${base}?${search.toString()}`);
        },
        /** The people the log can show in App usage's window, for its person filter. */
        people: (params: AppUsageParams) =>
            api<AppSessionPerson[]>(`${base}/people?${appUsageSearch(params).toString()}`),
        /** Every row in App usage's window, for its Export; at most 90 days of the log. */
        window: (params: AppUsageParams) =>
            api<AppSession[]>(`${base}/window?${appUsageSearch(params).toString()}`),
    },
    appUsage: (params: AppUsageParams) =>
        api<AppUsageReport>(`/api/reports/app-usage?${appUsageSearch(params).toString()}`),
    clientHeatmap: {
        report: (params: ClientHeatmapParams) =>
            api<ClientHeatmapReport>(
                `/api/reports/client-heatmap?${heatmapSearch(params).toString()}`,
            ),
        /** One page of the log behind Show all, narrowed by a client's name. */
        log: ({ page, size, q, ...params }: ActivityLogFilters) => {
            const search = heatmapSearch(params);
            search.set('page', String(page));
            search.set('size', String(size));
            if (q.trim()) search.set('q', q.trim());
            return api<ActivityLogPage>(`/api/reports/client-heatmap/log?${search.toString()}`);
        },
        lookbookPages: ({ weeks, lookbookId }: LookbookAttentionParams) => {
            const search = new URLSearchParams({ weeks: String(weeks) });
            if (lookbookId !== undefined) search.set('lookbookId', String(lookbookId));
            return api<LookbookAttention>(
                `/api/reports/client-heatmap/lookbook-pages?${search.toString()}`,
            );
        },
    },
    overview: (period: ReportPeriod) =>
        api<ReportOverview>(`/api/reports/overview?${new URLSearchParams({ period }).toString()}`),
    dashboardSales: ({ range, from, to }: DashboardSalesParams) => {
        const search = new URLSearchParams({ range });
        if (range === DASHBOARD_RANGE.Custom && from && to) {
            search.set('from', from);
            search.set('to', to);
        }
        return api<DashboardSales>(`/api/reports/dashboard-sales?${search.toString()}`);
    },
    transactions: (period: ReportPeriod) =>
        api<TransactionsReport>(
            `/api/reports/transactions?${new URLSearchParams({ period }).toString()}`,
        ),
    clients: (params: ClientsParams) =>
        api<ClientsReport>(`/api/reports/clients?${clientsSearch(params).toString()}`),
    orderQueue: (params: OrderQueueParams) => {
        const search = orderQueueSearch(params);
        return api<OrderQueue>(`/api/reports/transactions/orders${search ? `?${search}` : ''}`);
    },
    tax: (period: TaxPeriod) =>
        api<TaxReport>(`/api/reports/tax?${new URLSearchParams({ period }).toString()}`),
    brands: () => api<ReportBrands>('/api/reports/brands'),
    brandIntelligence: ({ brandId, quarter }: BrandIntelligenceParams) => {
        const search = new URLSearchParams({ brandId: String(brandId) });
        if (quarter) search.set('quarter', quarter);
        return api<BrandIntelligence>(`/api/reports/brand-intelligence?${search.toString()}`);
    },
};
